import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Flag, Loader2 } from "lucide-react";

const REASONS = [
  "Spam or misleading",
  "Inappropriate or offensive",
  "Fraud or scam",
  "Harassment",
  "Copyright or IP",
  "Other",
];

const TYPE_LABEL = {
  listing: "listing",
  event: "event",
  job: "job",
  need: "post",
  business: "business",
  user: "member",
};

export default function ReportDialog({ open, onOpenChange, targetType, targetId, targetTitle }) {
  const [reason, setReason] = useState(REASONS[0]);
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/reports", { target_type: targetType, target_id: targetId, reason, details });
      toast.success("Thanks — our moderators will review this shortly");
      setDetails("");
      onOpenChange?.(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send report");
    } finally {
      setSubmitting(false);
    }
  };

  const kindLabel = TYPE_LABEL[targetType] || "post";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1E3A2F] flex items-center gap-2">
            <Flag className="w-5 h-5 text-[#C05A3D]" /> Report this {kindLabel}
          </DialogTitle>
          <DialogDescription>
            {targetTitle ? <span className="italic">"{targetTitle}"</span> : "Tell our moderators what's wrong. Reports stay confidential."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-sm text-[#1E3A2F]">Reason</Label>
            <div className="mt-2 grid grid-cols-1 gap-1.5">
              {REASONS.map((r) => (
                <label key={r} className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer text-sm ${reason === r ? "border-[#C05A3D] bg-[#C05A3D]/5" : "border-[#E2DED0] hover:border-[#1E3A2F]/40"}`}>
                  <input
                    type="radio"
                    name="report-reason"
                    value={r}
                    checked={reason === r}
                    onChange={() => setReason(r)}
                    data-testid={`report-reason-${r.replace(/\s+/g, "-").toLowerCase()}`}
                    className="accent-[#C05A3D]"
                  />
                  {r}
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="report-details" className="text-sm text-[#1E3A2F]">Add context (optional)</Label>
            <Textarea
              id="report-details"
              data-testid="report-details-input"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="What should our team know?"
              className="mt-1 min-h-[90px]"
              maxLength={800}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange?.(false)} data-testid="report-cancel-btn">Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="report-submit-btn" className="abm-btn-primary rounded-full">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Flag className="w-4 h-4 mr-2" />}
              Send report
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
